import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress, Tabs,Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchClaimDetails, FetchClaimDetailsResponse } from 'common/services/api/Claims/Claims.thunk';
import { getLastPathSegment } from 'common/utils/locationHelpers';

const CLAIM_DETAILS_INLINE_STYLES = {
    marginLeft: '1.75rem'
};

export function ClaimDetails(): JSX.Element {
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Users);
    const [processing, setProcessing] = useState<boolean>(true);
    const location = useLocation();
    const [error, setError] = useState<string>('');
    const params = useParams();

    useEffect(() => {
        const lastPathSegment = getLastPathSegment();
        setCurrentTab(lastPathSegment as Routes);
    }, [location]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        const fetchParams = {
            id: params?.claimId ?? '',
            tenantId: params?.tenantId ?? '',
            applicationSuiteId: params?.suiteId ?? ''
        };
        dispatch(fetchClaimDetails(fetchParams))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    const getTabLink = (key: string) => (
        `/${Routes.ApplicationSuites}/${params.suiteId}/${Routes.Tenants}/${params.tenantId}/claims/${params.claimId}/${key}`
    );

    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        setCurrentTab(newValue);
    };

    const claimDetails = useAppSelector(selectPageInfo) as FetchClaimDetailsResponse;
    const errorCode = extractStatusCode(error);

    if (errorCode === 404) {
        return <NotFound />;
    }

    if (errorCode && errorCode !== 200) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    return (
        <Page
            title={`${claimDetails?.name} - ${RoutesNameMap.get(Routes.Claim)}`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {claimDetails?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceId: params.suiteId ?? '',
                                resourceName: claimDetails?.applicationSuiteName || ''
                            },{
                                resourceId: params.tenantId ?? '',
                                resourceName: claimDetails?.tenantName || ''
                            },{
                                resourceId: params.claimId ?? '',
                                resourceName: claimDetails?.name || ''
                            }]}
                        /> }
                    {claimDetails?.id &&
                    <>
                        <ResourceDetailHeader
                            resourceType='Claim'
                            resourceName={ claimDetails?.name || '' }
                            resourceId={ params.claimId ?? ''}
                        />
                        <ResourceDetailHeader
                            resourceType='Claim Data Type'
                            resourceId={claimDetails?.claimDataTypeName || ''}
                            shouldShowResourceName={false}
                            resourceTypeInlineStyles={CLAIM_DETAILS_INLINE_STYLES}
                            useDefaultResourceTypeTitle={false}
                            copyToClipboard={false}
                        />
                    </>
                    }
                </Typography>
            )}
        >
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '40rem' }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Users)}
                    key={Routes.Users}
                    link={getTabLink(Routes.Users)}
                    value={Routes.Users}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Roles)}
                    key={Routes.Roles}
                    link={getTabLink(Routes.Roles)}
                    value={Routes.Roles}
                />
            </Tabs>
            <Outlet />
        </Page>
    );
}
